import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { activeConditions } from "@app/components/dashboard-pathway/conditions";
import API from "@app/services/api";
import { useViewAsKey } from "@app/services/view-as";
import type {
  CompositionResponse,
  PathwayPatientsResponse,
  PathwayConditions,
  PathwayDimensionsResponse,
  PathwayFacetsResponse,
  PathwayPractitionersResponse,
  PathwaySummaryResponse,
  SingularityResponse,
} from "@app/types/dashboard-pathway";

/** The query every pathway route takes. One builder, so the cards and the rail cannot disagree. */
export interface PathwayQuery {
  /** Index signature so the query can be handed straight to the API layer without a cast. */
  [param: string]: string | undefined;
  filters?: string;
  /** Relative window token, resolved server-side against today — see `utils/periods.py`. */
  periode?: string;
  periodeStart?: string;
  periodeEnd?: string;
  scope?: string;
  /** A catalogue cohort: the board reads its population rather than the reader's practice. */
  cohortId?: string;
  /** A laboratory dataset: the board reads the dossiers it holds, like a cohort. */
  datasetId?: string;
  /** A practitioner's `User.id`: narrows the reader's perimeter. Admins and MAGE-X reviewers only (PT-111). */
  praticien?: string;
}

export function buildPathwayQuery(
  conditions: PathwayConditions,
  {
    periode,
    periodeStart,
    periodeEnd,
    scope,
    cohortId,
    datasetId,
    praticien,
  }: {
    periode?: string | null;
    periodeStart?: string | null;
    periodeEnd?: string | null;
    scope?: string;
    cohortId?: string;
    datasetId?: string;
    praticien?: string | null;
  },
): PathwayQuery {
  const query: PathwayQuery = {};
  const active = activeConditions(conditions);
  if (Object.keys(active).length > 0) query.filters = JSON.stringify(active);
  // A token and an explicit range are mutually exclusive — the server prefers the range, and sending
  // both would let a stale token silently win on one route and lose on another.
  if (periode && !periodeStart && !periodeEnd) query.periode = periode;
  if (periodeStart) query.periodeStart = periodeStart;
  if (periodeEnd) query.periodeEnd = periodeEnd;
  if (scope) query.scope = scope;
  if (cohortId) query.cohortId = cohortId;
  if (datasetId) query.datasetId = datasetId;
  if (praticien) query.praticien = praticien;
  return query;
}

interface Envelope<T> {
  ok?: boolean;
  data?: T;
  error?: string;
}

/**
 * One GET, re-run whenever its query changes, with the stale response discarded.
 *
 * The generation counter is not ceremony: the rail fires a request per click and the two cards read
 * the same query, so an earlier reply landing after a later one would leave the board showing a
 * selection nobody made. `error` distinguishes "the server said no" (a 403 outside the demo) from
 * "nothing to show", which the cards render differently.
 */
function usePathwayResource<T>(path: string, query: Record<string, unknown>, skip = false) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const serialised = JSON.stringify(query);
  /* The board also re-reads when the EYE moves, and that is not the same thing as a query change.
     Every route below answers for whoever the session is previewing, while the query string says
     nothing about it — so switching profile left the whole board showing the previous reader's
     cohort, dimension catalogue included, until a filter was touched or the page reloaded. */
  const viewAs = useViewAsKey();
  const lastViewAs = useRef(viewAs);

  const reload = useCallback(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    if (lastViewAs.current !== viewAs) {
      // Not stale data: another reader's. Drop it rather than show it while the new one loads.
      lastViewAs.current = viewAs;
      setData(null);
      setError(null);
    }
    const mine = (generation.current += 1);
    setLoading(true);
    API.get({ path, query: JSON.parse(serialised) })
      .then((res: Envelope<T>) => {
        if (mine !== generation.current) return;
        if (res?.ok && res.data !== undefined) {
          setData(res.data);
          setError(null);
        } else {
          setError(res?.error || "unavailable");
        }
      })
      .catch(() => {
        if (mine !== generation.current) return;
        setError("unavailable");
      })
      .finally(() => {
        if (mine === generation.current) setLoading(false);
      });
  }, [path, serialised, skip, viewAs]);

  useEffect(reload, [reload]);
  return { data, loading, error, reload };
}

/** The axes a card can be built on — a cohort's or a dataset's own when one is named. */
export function usePathwayDimensions(skip = false, cohortId?: string, datasetId?: string) {
  const query = useMemo<PathwayQuery>(
    () => ({
      ...(cohortId ? { cohortId } : {}),
      ...(datasetId ? { datasetId } : {}),
    }),
    [cohortId, datasetId],
  );
  return usePathwayResource<PathwayDimensionsResponse>("/dashboard/pathway/dimensions", query, skip);
}

export function usePathwaySummary(query: PathwayQuery, skip = false) {
  return usePathwayResource<PathwaySummaryResponse>("/dashboard/pathway/summary", query, skip);
}

export function usePathwayComposition(
  query: PathwayQuery,
  dims: string[],
  skip = false,
) {
  const full = useMemo(
    () => (dims.length > 0 ? { ...query, dims: dims.join(",") } : { ...query }),
    [query, dims],
  );
  return usePathwayResource<CompositionResponse>("/dashboard/pathway/composition", full, skip);
}

export function usePathwaySingularity(
  query: PathwayQuery,
  family: "acte" | "patient" | null | undefined,
  skip = false,
) {
  const full = useMemo(
    () => (family ? { ...query, famille: family } : { ...query }),
    [query, family],
  );
  return usePathwayResource<SingularityResponse>("/dashboard/pathway/singularity", full, skip);
}

/** Only fetched while the drill-down is open: it is a list nobody reads until they ask for it. */
export function usePathwayPatients(query: PathwayQuery, skip = false) {
  return usePathwayResource<PathwayPatientsResponse>("/dashboard/pathway/patients", query, skip);
}

export function usePathwayFacets(query: PathwayQuery, skip = false) {
  return usePathwayResource<PathwayFacetsResponse>("/dashboard/pathway/facets", query, skip);
}

const NO_QUERY: PathwayQuery = {};

/** The practitioners the board may be narrowed to — admins and MAGE-X reviewers only; the server refuses others. */
export function usePathwayPractitioners(skip = false) {
  return usePathwayResource<PathwayPractitionersResponse>(
    "/dashboard/pathway/practitioners",
    NO_QUERY,
    skip,
  );
}

/** The rolling windows `utils/periods.py` resolves against the day the question is asked. */
const ROLLING_PERIODS = new Set(["7j", "30j", "3m", "6m", "1a", "a_venir"]);

function isoDay(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function monthOf(year: number, month: number): { start: string; end: string } {
  // Day 0 of the next month is the last day of this one.
  return {
    start: isoDay(year, month, 1),
    end: isoDay(year, month, new Date(year, month, 0).getDate()),
  };
}

/**
 * The period a natural-language search names, as the board applies it: a rolling window stays a
 * token, resolved by the server against today and shown as the rail's chip; a year or a month becomes
 * its two dates, shown in the rail's date fields. `null` for anything else, which the caller reports
 * rather than drops — « juillet 2026 » and « l'année dernière » were dropped in silence.
 */
export function searchPeriod(
  value: string,
  today: Date = new Date(),
): { token: string } | { start: string; end: string } | null {
  const text = value.trim();
  if (ROLLING_PERIODS.has(text)) return { token: text };
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  if (text === "annee_courante")
    return { start: `${year}-01-01`, end: `${year}-12-31` };
  if (text === "annee_derniere")
    return { start: `${year - 1}-01-01`, end: `${year - 1}-12-31` };
  if (text === "mois_courant") return monthOf(year, month);
  if (text === "mois_dernier")
    return month === 1 ? monthOf(year - 1, 12) : monthOf(year, month - 1);
  const named = /^(\d{4})-(\d{2})$/.exec(text);
  if (named) {
    const namedMonth = Number(named[2]);
    return namedMonth >= 1 && namedMonth <= 12
      ? monthOf(Number(named[1]), namedMonth)
      : null;
  }
  if (/^\d{4}$/.test(text) && Number(text) > 1900)
    return { start: `${text}-01-01`, end: `${text}-12-31` };
  return null;
}
